/**
 * useDeepLinking Hook
 *
 * Handles deep linking from Shopify Admin Action extension.
 * Detects URL parameters and triggers appropriate modal/state changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Return type for useDeepLinking hook
 */
export interface UseDeepLinkingReturn {
  /** Pre-selected product ID from deep link (for AddProductModal) */
  preSelectedProductId: string | undefined;
  /** Whether the add product modal should be opened via deep link */
  shouldOpenAddProductModal: boolean;
  /** Clear the deep link state after handling */
  clearDeepLink: () => void;
  /** Clear the pre-selected product ID */
  clearPreSelectedProductId: () => void;
}

/**
 * Hook for handling deep linking from Shopify Admin Action extension
 *
 * Detects openModal and productId URL parameters and provides state
 * for triggering the appropriate modal with pre-selection.
 *
 * @returns Deep link state and handlers
 */
export function useDeepLinking(): UseDeepLinkingReturn {
  const [searchParams] = useSearchParams();
  const [preSelectedProductId, setPreSelectedProductId] = useState<string | undefined>(undefined);
  const [shouldOpenAddProductModal, setShouldOpenAddProductModal] = useState(false);

  // Handle deep linking from Shopify Admin Action extension
  // Modal now fetches its own data, so we just set preselection and open directly
  useEffect(() => {
    const openModal = searchParams.get('openModal');
    const productId = searchParams.get('productId');

    if (openModal === 'addProduct') {
      if (productId) {
        setPreSelectedProductId(productId);
      }
      setShouldOpenAddProductModal(true);

      // Clean up URL params
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('openModal');
      newUrl.searchParams.delete('productId');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams]);

  const clearDeepLink = useCallback(() => {
    setShouldOpenAddProductModal(false);
  }, []);

  const clearPreSelectedProductId = useCallback(() => {
    setPreSelectedProductId(undefined);
  }, []);

  return {
    preSelectedProductId,
    shouldOpenAddProductModal,
    clearDeepLink,
    clearPreSelectedProductId,
  };
}
